import { useEffect, useMemo, useState } from "react";
import type { DashboardSection, DashboardSnapshot } from "../lib/api";
import { ProductSection } from "./ProductSection";
import { FeatureRequestModal } from "./FeatureRequestModal";
import { loadJSON, saveJSON, STORAGE_KEYS } from "../lib/storage";

interface Props {
  data: DashboardSnapshot | undefined;
  isLoading: boolean;
}

/**
 * The "Marktpreise" view: card grid of product sections, fed by
 * /api/dashboard/snapshot.
 *
 * Sub-features:
 *   - Drag-and-drop reordering of the cards (HTML5 native DnD, no extra
 *     dependency). The chosen order is persisted in localStorage so
 *     reloading the page keeps the user's preferred layout. The
 *     server-side order from the snapshot is the default and is used as
 *     a fallback whenever a previously persisted id is no longer
 *     produced by the worker (graceful migration if a section is
 *     removed/renamed).
 *   - `items-start` on the grid so a small card (2-row Spot) doesn't get
 *     stretched to the height of a 17-row Futures card next to it. The
 *     result is a slightly ragged but information-dense grid — and the
 *     drag-and-drop lets the user line up cards by size if desired.
 *   - "Feature anfragen"-Button below the grid posts directly to GitHub.
 */
export function MarketPricesTab({ data, isLoading }: Props) {
  const [frOpen, setFrOpen] = useState(false);
  const [order, setOrder] = useState<string[] | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Load persisted order once on mount.
  useEffect(() => {
    setOrder(loadJSON<string[] | null>(STORAGE_KEYS.marketPricesOrder, null));
  }, []);

  // Resolve the effective render order: persisted ids first (filtered to
  // those still present in the snapshot), then any new ids from the
  // snapshot that aren't in the persisted list (so newly-added sections
  // appear without needing a reset).
  const orderedSections: DashboardSection[] = useMemo(() => {
    if (!data) return [];
    if (!order || order.length === 0) return data.sections;
    const byId = new Map(data.sections.map((s) => [s.id, s]));
    const seen = new Set<string>();
    const out: DashboardSection[] = [];
    for (const id of order) {
      const sec = byId.get(id);
      if (sec) {
        out.push(sec);
        seen.add(id);
      }
    }
    for (const sec of data.sections) if (!seen.has(sec.id)) out.push(sec);
    return out;
  }, [data, order]);

  const persistOrder = (next: string[]) => {
    setOrder(next);
    saveJSON(STORAGE_KEYS.marketPricesOrder, next);
  };

  const reorder = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const current = orderedSections.map((s) => s.id);
    const fromIdx = current.indexOf(sourceId);
    const toIdx = current.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = current.slice();
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, sourceId);
    persistOrder(next);
  };

  const resetOrder = () => persistOrder(data ? data.sections.map((s) => s.id) : []);

  if (!data && isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 items-start animate-pulse">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-72 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
          />
        ))}
      </div>
    );
  }
  if (!data) return null;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 items-start">
        {orderedSections.map((section) => (
          <ProductSection
            key={section.id}
            section={section}
            draggable
            isDragging={dragId === section.id}
            isDragTarget={overId === section.id && dragId !== section.id}
            onDragStart={() => setDragId(section.id)}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            onDragEnter={() => setOverId(section.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragId) reorder(dragId, section.id);
              setDragId(null);
              setOverId(null);
            }}
          />
        ))}
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {order && order.length > 0 && (
          <button
            type="button"
            onClick={resetOrder}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            title="Reihenfolge auf den Server-Default zurücksetzen"
          >
            Reihenfolge zurücksetzen
          </button>
        )}
        <button
          type="button"
          onClick={() => setFrOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-sm transition"
        >
          <PlusIcon />
          Feature anfragen
        </button>
      </div>
      <FeatureRequestModal open={frOpen} onClose={() => setFrOpen(false)} />
    </>
  );
}

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
